/**
 * Turn clips from the quick look into a real video file: one clip (⬇ on a card) or several
 * stitched in pick order (✂ Stitch). It's the same `make-video` export the sheet and the Top 20
 * page use, so the worker cuts it and it also lands in Exports. The make-video answer carries no
 * id, so we remember which exports already existed and watch Exports for the new one with our
 * title; when it's ready its card shows Download.
 *
 * Postable by default, like the Top 20 page: burned captions in the cherry style, unless the
 * person's My Style says otherwise. Never sent to Auto (auto_post=false).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import apiRequest from "../../../../utils/apiClient";
import { getMyCreationsUrl, makeVideoUrl } from "../../../constants";
import { captionFormFields, loadMyStyle } from "../../../grid/export/myStyle";
import { ReelClip } from "./topClips";

export type JobState = "making" | "ready" | "failed";
export interface Job {
  state: JobState;
  url?: string;
  title: string;
}

interface Creation {
  id: number;
  title: string;
  video: string;
  status?: string;
}

const POLL_MS = 4000;
const GIVE_UP_MS = 15 * 60 * 1000;

const listCreations = async (): Promise<Creation[]> => {
  const res = await apiRequest(getMyCreationsUrl, { method: "GET" });
  if (!res.ok) throw new Error(String(res.status));
  const body = await res.json();
  return Array.isArray(body) ? body : body.creations || [];
};

export const useClipMaker = (transcriptId: number) => {
  const [jobs, setJobs] = useState<Record<string, Job>>({});
  const watching = useRef<Record<string, { title: string; before: Set<number>; since: number }>>({});
  const style = useRef<Record<string, any> | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadMyStyle().then((s: any) => { if (!cancelled) style.current = s || {}; }).catch(() => { style.current = {}; });
    return () => { cancelled = true; };
  }, []);

  // One poll for every job in flight.
  useEffect(() => {
    const timer = setInterval(async () => {
      const keys = Object.keys(watching.current);
      if (!keys.length) return;
      let creations: Creation[];
      try { creations = await listCreations(); } catch { return; }
      keys.forEach((key) => {
        const w = watching.current[key];
        const found = creations.find((c) => !w.before.has(Number(c.id)) && c.title === w.title);
        const done = (state: JobState, url?: string) => {
          delete watching.current[key];
          setJobs((j) => ({ ...j, [key]: { state, url, title: w.title } }));
        };
        if (found && (found.status === "ready" || (!found.status && found.video))) done("ready", found.video);
        else if (found && (found.status === "failed" || found.status === "cancelled")) done("failed");
        else if (Date.now() - w.since > GIVE_UP_MS) done("failed");
      });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, []);

  /** `key` names the job on screen (a clip id, "stitch", or a Super Cut). `superCut` = the searched
   *  words: the worker cuts only those words out of each clip, the full sheet's Super Cut. */
  const make = useCallback(async (key: string, clips: ReelClip[], title: string, superCut?: string): Promise<void> => {
    if (!clips.length) return;
    setJobs((j) => ({ ...j, [key]: { state: "making", title } }));
    try {
      const before = new Set((await listCreations()).map((c) => Number(c.id)));
      const s = style.current || {};
      const captions = typeof s.burn_captions === "boolean" ? s.burn_captions : true;
      const body = new URLSearchParams();
      body.append("list_of_ids", clips.map((c) => c.id).join(","));
      body.append("transcript_id", String(transcriptId));
      body.append("download_id", String(transcriptId));
      body.append("title", title);
      body.append("search_text", superCut || "");
      body.append("model", clips[0].model);
      body.append("kind", superCut ? "mp4_super_cut" : "mp4");
      body.append("orientation", s.orientation || "original");
      body.append("auto_post", "false");
      if (captions) {
        body.append("burn_captions", "true");
        body.append("caption_font_key", "sans");
        body.append("caption_size", "64");
        body.append("caption_mode", "group");
        body.append("words_per_group", "3");
        body.append("caption_style", s.caption_style || "cherry");
      }
      Object.entries(captionFormFields(s) as Record<string, string>).forEach(([k, v]) => {
        if (captions || k === "intro" || k === "logo") body.set(k, v);
      });
      body.append("adjusted_times", "{}");
      body.append("segments", "[]");
      const res = await apiRequest(makeVideoUrl, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      if (!res.ok) throw new Error(String(res.status));
      watching.current[key] = { title, before, since: Date.now() };
    } catch {
      setJobs((j) => ({ ...j, [key]: { state: "failed", title } }));
    }
  }, [transcriptId]);

  /** Drop a finished job (the picks behind a stitch changed). */
  const forget = useCallback((key: string) => {
    delete watching.current[key];
    setJobs((j) => { const n = { ...j }; delete n[key]; return n; });
  }, []);

  return { jobs, make, forget };
};
